/**
 * Korrelation zwischen zwei Kursreihen — die Zahl hinter „das ist doch
 * dasselbe Papier in Grün".
 *
 * Der Themenklumpen (`risiko.ts`) fängt, was getaggt ist. Er fängt NICHT den
 * Fall, den man nicht kommen sieht: zwei Halbleiterwerte ohne Tag, eine Aktie
 * und der Sektor-ETF, der sie zur Hälfte enthält, zwei Ölwerte in
 * verschiedenen Ländern. Dafür braucht es die gemessene Ähnlichkeit der
 * Tagesbewegungen.
 *
 * ZWEI DINGE, DIE HIER LEICHT FALSCH GEMACHT WERDEN
 *
 * 1. NICHT AUF PREISEN RECHNEN, SONDERN AUF RENDITEN. Zwei Aktien, die beide
 *    über ein Jahr steigen, haben eine Preis-Korrelation nahe 1 — auch wenn
 *    sie sich an keinem einzigen Tag gleich bewegen. Gemeint ist aber genau
 *    das: fallen sie am selben Tag zusammen? Das steht in den Tagesrenditen.
 *
 * 2. NACH DATUM PAAREN, NICHT NACH POSITION. Zwei Reihen haben nicht dieselben
 *    Handelstage — Feiertage, verspätete Bars, ein Wert seit Kurzem gelistet.
 *    Wer stumpf Index gegen Index rechnet, vergleicht Dienstag mit Mittwoch
 *    und bekommt eine plausible, falsche Zahl. Deshalb wird über das Datum
 *    geschnitten.
 *
 * Diese Datei rechnet nur. Wo die Grenze liegt und ob sie sperrt, steht in
 * `risiko.ts`.
 */

export interface KursPunkt {
  /** `YYYY-MM-DD` — auch längere ISO-Zeitstempel werden auf den Tag gekürzt. */
  timestamp: string;
  close: number;
}

/** Wie viele gemeinsame Renditen mindestens vorliegen müssen. */
export const KORRELATION_MIN_PUNKTE = 40;

/** Standardfenster in Handelstagen — rund ein Quartal. */
export const KORRELATION_FENSTER = 90;

/**
 * Ab hier wird die Ähnlichkeit genannt — als HINWEIS, nicht als Sperre.
 *
 * HERKUNFT: Messung vom 12.08.2026
 * (`docs/konzepte/2026-08-12-korrelation-schwelle.md`, 633 Signale).
 *
 * WARUM KEINE SPERRE (Entscheidung Ronni, 12.08.): Jede feste Schwelle greift
 * bevorzugt im Marktstress — bei 0,80 in 32,4 % der Fälle gegenüber 13,7 % im
 * ruhigen Markt, bei 0,75 sogar in 43,2 %. Also genau dann, wenn die
 * antizyklische Regel kaufen will. Ob zwei ähnliche Werte zusammen ins Depot
 * dürfen, ist damit eine Abwägung und keine Rechnung; sie bleibt beim Trader.
 * Die Maschine liefert die Zahl und den Partner, mehr nicht.
 *
 * 0,75 als Schwelle des Erwähnens: Der Median der höchsten Korrelation zum
 * Bestand liegt bei 0,51 (im Stress 0,72). Darunter wäre der Hinweis der
 * Normalfall und damit wertlos.
 */
export const KORRELATION_HINWEIS = 0.75;

/**
 * Ab hier ist es kein ähnlicher Wert mehr, sondern faktisch derselbe.
 *
 * Kein zweiter Hinweis und keine zweite Regel — nur eine schärfere Formulierung
 * desselben einen Hinweises. In der Messung stehen hier die Doppelkäufe: SPY
 * gegen IWM, QQQ gegen SMH, Visa gegen Mastercard, Chevron gegen Exxon. Das
 * ist eine andere Information als „läuft ähnlich", und sie gehört gesagt.
 */
export const KORRELATION_DOPPELT = 0.9;

function tag(ts: string): string {
  return ts.slice(0, 10);
}

/**
 * Tagesrenditen je Datum.
 *
 * Der Schlüssel ist der Tag der SPÄTEREN Bar: Die Rendite von Montag auf
 * Dienstag gehört zu Dienstag. Nur so paaren zwei Reihen dieselbe Bewegung.
 */
export function renditenNachTag(bars: readonly KursPunkt[]): Map<string, number> {
  const out = new Map<string, number>();
  // Aufsteigend sortieren, statt sich auf die Reihenfolge der Quelle zu
  // verlassen — der eine Anbieter liefert alt→neu, der andere umgekehrt.
  const sortiert = [...bars]
    .filter((b) => Number.isFinite(b.close) && b.close > 0)
    .sort((a, b) => tag(a.timestamp).localeCompare(tag(b.timestamp)));

  for (let i = 1; i < sortiert.length; i++) {
    const vorher = sortiert[i - 1]!;
    const jetzt = sortiert[i]!;
    const d1 = tag(vorher.timestamp);
    const d2 = tag(jetzt.timestamp);
    // Doppelte Bars zum selben Tag ergeben eine Rendite von 0 und würden die
    // Korrelation künstlich nach oben ziehen.
    if (d1 === d2) continue;
    out.set(d2, jetzt.close / vorher.close - 1);
  }
  return out;
}

/**
 * Pearson-Korrelation. `null`, wenn eine Seite sich nicht bewegt hat —
 * dann ist sie nicht definiert, und 0 wäre eine Behauptung.
 */
export function pearson(a: readonly number[], b: readonly number[]): number | null {
  const n = Math.min(a.length, b.length);
  if (n < 2) return null;

  let sa = 0;
  let sb = 0;
  for (let i = 0; i < n; i++) {
    sa += a[i]!;
    sb += b[i]!;
  }
  const ma = sa / n;
  const mb = sb / n;

  let zaehler = 0;
  let qa = 0;
  let qb = 0;
  for (let i = 0; i < n; i++) {
    const da = a[i]! - ma;
    const db = b[i]! - mb;
    zaehler += da * db;
    qa += da * da;
    qb += db * db;
  }
  if (qa <= 0 || qb <= 0) return null;

  const r = zaehler / Math.sqrt(qa * qb);
  // Rundungsfehler können knapp über 1 hinausschießen.
  return Math.max(-1, Math.min(1, r));
}

/**
 * Korrelation zweier Kursreihen über die letzten `fenster` GEMEINSAMEN Tage.
 *
 * `null` heißt „nicht bestimmbar" — zu wenig Überschneidung. Das ist kein
 * Grund zu sperren, aber auch kein Freibrief; der Aufrufer zeigt es an.
 */
export function korrelation(
  a: readonly KursPunkt[],
  b: readonly KursPunkt[],
  fenster = KORRELATION_FENSTER,
  minPunkte = KORRELATION_MIN_PUNKTE
): number | null {
  const ra = renditenNachTag(a);
  const rb = renditenNachTag(b);

  const gemeinsam = [...ra.keys()].filter((d) => rb.has(d)).sort();
  if (gemeinsam.length < minPunkte) return null;

  const fensterTage = gemeinsam.slice(-fenster);
  return pearson(
    fensterTage.map((d) => ra.get(d)!),
    fensterTage.map((d) => rb.get(d)!)
  );
}
